import { IconButton, LuneThemeNew, Text, Tooltip } from '@lune-fe/lune-ui-lib'
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined'
import { Link, SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import { FC, ReactNode } from 'react'

import WhiteBoxContainer from 'components/WhiteBoxContainer'

const { palette } = LuneThemeNew

const WhiteBoxContainerWithContent: FC<{
    children: ReactNode
    title: string
    subTitle?: string
    sx?: SxProps
    /**
     * If true, removes padding from the body section (useful for full-width content like tables)
     */
    fullBody?: boolean
    /**
     * Optional link displayed in the header (top-right)
     * Shows as an underlined text link that changes color on hover
     */
    link?: {
        href: string
        label: string
    }
    /**
     * Optional click handler for the container
     * When provided, displays a floating arrow button in the bottom-right
     */
    onClick?: () => void
    /**
     * Optional tooltip label shown when hovering the action button
     * Only applies when `onClick` is set
     */
    buttonTooltipLabel?: string
}> = ({ children, fullBody = false, title, subTitle, link, buttonTooltipLabel, onClick, sx }) => {
    return (
        <WhiteBoxContainer
            sx={{
                width: '100%',
                ...sx,
            }}
        >
            <Box
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        flexDirection: 'column',
                    }}
                >
                    <Text variant={'body2-medium'}>{title}</Text>
                    {subTitle && <Text variant={'body3'}>{subTitle}</Text>}
                </Box>
                {link && (
                    <Link
                        href={link.href}
                        sx={{
                            color: palette.TextSecondary,
                            '.MuiTypography-root': {
                                color: palette.TextSecondary,
                            },
                            textDecoration: 'underline',
                            textDecorationColor: palette.TextSecondary,
                            '&:hover': {
                                '.MuiTypography-root': {
                                    color: palette.TextPrimary,
                                },
                                textDecorationColor: palette.TextPrimary,
                            },
                        }}
                    >
                        <Text variant={'body3'}>{link.label}</Text>
                    </Link>
                )}
            </Box>
            <Box
                sx={{
                    p: fullBody ? 0 : 2,
                    pt: 0,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    gap: 1,
                }}
            >
                {children}
                {onClick && (
                    <>
                        {buttonTooltipLabel ? (
                            <Tooltip title={buttonTooltipLabel} placement={'top'}>
                                <IconButton
                                    onClick={onClick}
                                    leadingIcon={<ArrowForwardOutlinedIcon />}
                                    size={'sm'}
                                    style={'floating'}
                                />
                            </Tooltip>
                        ) : (
                            <IconButton
                                onClick={onClick}
                                leadingIcon={<ArrowForwardOutlinedIcon />}
                                size={'sm'}
                                style={'floating'}
                            />
                        )}
                    </>
                )}
            </Box>
        </WhiteBoxContainer>
    )
}

export default WhiteBoxContainerWithContent
